import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { serverDateFormat, EntityType } from '@components/shared/constants';
import { useCurrentHotel } from '@hooks/useCurrentHotel';
import { onError } from '@components/shared/messages';
import { useLoader } from '@hooks/useLoader';
import { RateAssignment } from '@/api/services/next';
import { RestrictionDetails } from '@/features/restriction-details/services/restriction-details-service';

const nonEmpty = (ids) => (ids?.length ? ids : undefined);

export const buildSearchSpec = (state) => {
    const [fromDate, toDate] = state.FromToDates || [];
    const [createFromDate, createToDate] = state.CreateFromToDates || [];
    const productsEnabled = state.RoomTypeIds?.length && state.RateIds?.length;
    return {
        HotelIds: nonEmpty(state.HotelIds),
        RoomTypeIds: nonEmpty(state.RoomTypeIds),
        CustomerIds: nonEmpty(state.CustomerIds),
        RateIds: nonEmpty(state.RateIds),
        ProductIds: productsEnabled ? nonEmpty(state.ProductIds) : undefined,
        FromDate: fromDate,
        ToDate: toDate,
        CreateFromDate: createFromDate || undefined,
        CreateToDate: createToDate || createFromDate || undefined,
        IncludeInactive: Boolean(state.IncludeInactive),
        Inclusive: Boolean(state.Inclusive),
    };
};

export const validateSearch = (state, msg) => {
    const [fromDate, toDate] = state.FromToDates || [];
    if (!state.HotelIds?.length || !fromDate || !toDate) {
        const error = new Error(msg('MandatoryFieldsMissing'));
        error.status = 400;
        throw error;
    }
    return true;
};

export const getAssignedCustomerIds = (rateAssignments) =>
    _.uniq((rateAssignments || []).map((ra) => ra.EntityId));

// Customers dropdown props: all customers when no rate is picked, otherwise only those assigned to the picked rates.
export const buildCustomersProps = (rateIds, assignedCustomerIds) => {
    if (!rateIds?.length) return { spec: undefined, data: undefined };
    if (!assignedCustomerIds) return { spec: undefined, data: [] };
    return assignedCustomerIds.length
        ? { spec: { Ids: assignedCustomerIds }, data: undefined }
        : { spec: undefined, data: [] };
};

export function useRestrictionDetailsPageStates() {
    const currentHotel = useCurrentHotel();
    const [initial] = useState(() => {
        const today = moment().format(serverDateFormat);
        const hotels = currentHotel?.Id ? [currentHotel.Id] : [];
        return { HotelIds: hotels, FromToDates: [today, today], IncludeInactive: false, Inclusive: true };
    });
    const [hotelIds, setHotelIds] = useState(initial.HotelIds);
    const [roomTypeIds, setRoomTypeIds] = useState([]);
    const [rateIds, setRateIds] = useState([]);
    const [assignedCustomerIds, setAssignedCustomerIds] = useState();
    const [data, setData] = useState([]);
    const [searchedFromDate, setSearchedFromDate] = useState(initial.FromToDates[0]);
    const [showRestrictionsPopup, setShowRestrictionsPopup] = useState(false);
    const changeFieldRef = useRef(null);
    const doSearchRef = useRef(null);
    const lastSpecRef = useRef(null);
    const loader = useLoader();

    const setDoSearchRef = useCallback((doSearch) => {
        doSearchRef.current = doSearch;
    }, []);

    const setChangeFieldRef = useCallback((changeField) => {
        changeFieldRef.current = changeField;
    }, []);

    // Goes through SearchPanel so its own field values are cleared too, not just our local state.
    const clearFields = useCallback((keys) => {
        keys.forEach((key) => changeFieldRef.current?.(key, []));
    }, []);

    const handleHotelIdsChange = useCallback((ids) => {
        setHotelIds(ids || []);
        clearFields(['RoomTypeIds', 'RateIds', 'CustomerIds', 'ProductIds']);
    }, [clearFields]);

    const handleRoomTypeIdsChange = useCallback((ids) => {
        setRoomTypeIds(ids || []);
        clearFields(['RateIds', 'CustomerIds', 'ProductIds']);
    }, [clearFields]);

    const handleRateIdsChange = useCallback((ids) => {
        setRateIds(ids || []);
        clearFields(['CustomerIds', 'ProductIds']);
    }, [clearFields]);

    useEffect(() => {
        setAssignedCustomerIds(undefined);
        if (!rateIds.length) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const assignments = await RateAssignment.GetRateAssignments({
                    EntityTypeId: EntityType.Customer,
                    RateIds: rateIds,
                    SkipCache: false,
                    Includes: [0],
                });
                if (!cancelled) setAssignedCustomerIds(getAssignedCustomerIds(assignments));
            } catch (error) {
                if (!cancelled) onError(error);
            }
        })();
        return () => { cancelled = true; };
    }, [rateIds]);

    const customersProps = useMemo(
        () => buildCustomersProps(rateIds, assignedCustomerIds),
        [rateIds, assignedCustomerIds]
    );

    const roomTypesSpec = useMemo(() => ({ HotelIds: hotelIds }), [hotelIds]);

    const ratesSpec = useMemo(
        () => (roomTypeIds.length ? { RoomTypeIds: roomTypeIds } : undefined),
        [roomTypeIds]
    );

    const productsSpec = useMemo(
        () => ({ HotelIds: hotelIds, RoomTypeIds: roomTypeIds, RateIds: rateIds }),
        [hotelIds, roomTypeIds, rateIds]
    );

    // SearchPanel passes the built spec (FromDate/ToDate), not the raw FromToDates state.
    const handleSearchComplete = useCallback((result, spec) => {
        setData(result || []);
        if (spec) lastSpecRef.current = spec;
        if (spec?.FromDate) setSearchedFromDate(spec.FromDate);
    }, []);

    // SearchPanel's registered doSearch closes over its first-render state, so re-run the last spec directly.
    const handleRestrictionsSaved = useCallback(async () => {
        setShowRestrictionsPopup(false);
        if (!lastSpecRef.current) {
            doSearchRef.current?.();
            return;
        }
        loader(true);
        try {
            setData((await RestrictionDetails.search(lastSpecRef.current)) || []);
        } catch (error) {
            onError(error);
        } finally {
            loader(false);
        }
    }, [loader]);

    return {
        data,
        initial,
        hotelIds,
        roomTypeIds,
        rateIds,
        roomTypesSpec,
        ratesSpec,
        productsSpec,
        customersProps,
        popupHotelId: hotelIds.length === 1 ? hotelIds[0] : null,
        searchedFromDate,
        showRestrictionsPopup,
        setShowRestrictionsPopup,
        handleRestrictionsSaved,
        setDoSearchRef,
        setHotelIds: handleHotelIdsChange,
        setRoomTypeIds: handleRoomTypeIdsChange,
        setRateIds: handleRateIdsChange,
        setChangeFieldRef,
        search: RestrictionDetails.search,
        handleSearchComplete,
    };
}
